import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  TableCell,
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  TableSortLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AdminCustomersTableRow } from "./AdminCustomersTableRow";
import { AdminModel } from "../../../models/AdminModel";
import { IUser } from "../../../types";
import { useHistory } from "react-router-dom";

const readableSortColor = "rgba(0, 0, 0, 0.87)";
const readableSortIconColor = "rgba(0, 0, 0, 0.54)";
const sortLabelStyle: React.CSSProperties = { color: readableSortColor };

const useStyles = makeStyles({
  sortIcon: {
    color: `${readableSortIconColor} !important`,
  },
});

export const AdminCustomersTable: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [usersData, setUsersData] = useState<IUser[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  // pagination state
  const [page, setPage] = useState<number>(0); // zero-indexed for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // sort state
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // for debounce
  const searchDebounceRef = useRef<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const loadingTimerRef = useRef<number | null>(null);
  const hasMountedSearchEffectRef = useRef(false);
  const latestRequestIdRef = useRef(0);

  const handleCreateOrder = (user: IUser) => {
    history.push("/admin/create-order", { selectedUser: user });
  };

  // fetch page of users from server
  const reloadUsers = async (
    pageToLoad = page,
    limitToLoad = rowsPerPage,
    sValue = searchValue,
    sSortBy = sortBy,
    sSortOrder = sortOrder
  ) => {
    // Search and pagination can overlap; only the newest request should update the table.
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      // Delay the spinner for quick responses, but keep old timers from reviving loading state.
      loadingTimerRef.current = window.setTimeout(() => {
        if (latestRequestIdRef.current === requestId) {
          setShowLoading(true);
        }
      }, 200);

      // AdminModel.getAllCustomers expects page 1-indexed
      const resp = await AdminModel.getAllCustomers(
        pageToLoad + 1,
        limitToLoad,
        undefined,
        sValue || undefined,
        sSortBy || undefined,
        sSortBy ? sSortOrder : undefined
      );
      if (latestRequestIdRef.current !== requestId) {
        return;
      }
      setUsersData(resp.data);
      setTotal(resp.total);
      setPage(resp.page - 1); // server returns 1-indexed page
      setRowsPerPage(resp.limit);
    } catch (err) {
      if (latestRequestIdRef.current === requestId) {
        console.error("Failed to load users", err);
      }
    } finally {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
        setShowLoading(false);
      }
    }
  };

  // Debounced effect for searchValue changes
  useEffect(() => {
    // The initial load effect already fetches immediately; skip debounce once to avoid a duplicate request.
    if (!hasMountedSearchEffectRef.current) {
      hasMountedSearchEffectRef.current = true;
      return;
    }

    // Reset to the first page so each new search starts at the top of its results.
    setPage(0);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(() => {
      // when search changes, reset to first page
      reloadUsers(0, rowsPerPage, searchValue, sortBy, sortOrder);
    }, 400);

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // initial load
  useEffect(() => {
    reloadUsers(0, rowsPerPage, searchValue, sortBy, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    reloadUsers(newPage, rowsPerPage, searchValue, sortBy, sortOrder);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt((event.target as HTMLInputElement).value, 10);

    setRowsPerPage(newLimit);
    setPage(0);
    reloadUsers(0, newLimit, searchValue, sortBy, sortOrder);
  };

  const handleSort = (field: string) => {
    setPage(0);
    let newSortOrder: "asc" | "desc";
    if (sortBy === field) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    } else {
      newSortOrder = "asc";
    }
    setSortBy(field);
    setSortOrder(newSortOrder);
    reloadUsers(0, rowsPerPage, searchValue, field, newSortOrder);
  };

  const sortLabelClasses = { icon: classes.sortIcon };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {/* Left Side */}
        <Box>
          <a href="/admin/new-user">
            <Button color="primary" variant="contained" size="medium">
              Add a new user
            </Button>
          </a>
        </Box>

        {/* Right Side */}
        <Box
          display="flex"
          alignItems="center"
          flexGrow={1}
          justifyContent="flex-end"
          style={{ marginLeft: "3rem" }}
        >
          <TextField
            id="search-users"
            label="Search Users"
            type="search"
            variant="outlined"
            size="small"
            fullWidth
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
          />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "email"}
                  classes={sortLabelClasses}
                  direction={sortBy === "email" ? sortOrder : "asc"}
                  onClick={() => handleSort("email")}
                  style={sortLabelStyle}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>Given Name</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "familyName"}
                  classes={sortLabelClasses}
                  direction={sortBy === "familyName" ? sortOrder : "asc"}
                  onClick={() => handleSort("familyName")}
                  style={sortLabelStyle}
                >
                  Family Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Customer Notes</TableCell>
              <TableCell align="center">Role</TableCell>
              <TableCell align="center" colSpan={2}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : usersData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Data Available
                </TableCell>
              </TableRow>
            ) : (
              usersData.map((user) => (
                <AdminCustomersTableRow
                  key={user._id}
                  user={user}
                  reloadUsers={() => reloadUsers(page, rowsPerPage, searchValue, sortBy, sortOrder)}
                  onCreateOrder={handleCreateOrder}
                />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onChangePage={(_, newPage) => handleChangePage(_, newPage)}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={(event) => handleChangeRowsPerPage(event)}
          rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        />
      </TableContainer>
    </>
  );
};
